import { PageHeader, QrPreviewCard, QrPrintSheet, SelectField } from "@jod/ui";
import { createFileRoute } from "@tanstack/react-router";
import { useMemo, useState } from "react";
import { useOwnerState, useRotateQr } from "../../features/backoffice/queries.ts";
import { env } from "../../lib/env.ts";
import { buildTableQrUrl } from "../../lib/qr-url.ts";

export const Route = createFileRoute("/_app/qr")({ component: QrScreen });

function QrScreen() {
  const { data } = useOwnerState();
  const rotate = useRotateQr();
  const branches = data?.branches ?? [];
  const [branchFilter, setBranchFilter] = useState("all");

  const tables = data?.tables ?? [];
  const visibleTables = useMemo(
    () =>
      branchFilter === "all" ? tables : tables.filter((table) => table.branchId === branchFilter),
    [tables, branchFilter],
  );
  const branchOptions = [
    { value: "all", label: "All branches" },
    ...branches.map((branch) => ({ value: branch.id, label: branch.name })),
  ];
  const branchName = (branchId: string) => branches.find((branch) => branch.id === branchId)?.name;

  return (
    <div className="space-y-6">
      <PageHeader
        title="QR generation"
        subtitle="Real, scannable per-table QR codes encoding the opaque qr_token — never guessable venue/table IDs (FR-OWN-050/051)."
      />
      {branches.length > 1 ? (
        <div className="max-w-sm">
          <SelectField
            id="qrBranchFilter"
            label="Filter by branch"
            value={branchFilter}
            onChange={setBranchFilter}
            options={branchOptions}
          />
        </div>
      ) : null}
      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
        {visibleTables.map((table) => (
          <QrPreviewCard
            key={table.id}
            label={table.label}
            fileName={table.code}
            payload={buildTableQrUrl({
              baseUrl: env.customerAppUrl,
              qrToken: table.qrToken,
              branchId: table.branchId,
            })}
            onRotate={() => rotate.mutate(table.id)}
          />
        ))}
      </div>
      <PageHeader
        title="Print sheet"
        subtitle="A4, multiple tables per page, table label under each code (FR-OWN-052)."
      />
      <QrPrintSheet
        items={visibleTables.map((table) => ({
          id: table.id,
          label: `${table.label} (${table.code})`,
          payload: buildTableQrUrl({
            baseUrl: env.customerAppUrl,
            qrToken: table.qrToken,
            branchId: table.branchId,
          }),
        }))}
        subtitle={branchFilter === "all" ? undefined : branchName(branchFilter)}
        perPage={8}
      />
    </div>
  );
}
